import React from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Link, 
  Divider, 
  List, 
  ListItem, 
  ListItemText,
  Paper,
  Container
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#282828',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      marginBottom: '1rem',
      color: '#1976d2',
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      margin: '2rem 0 1rem',
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 500,
      margin: '1.5rem 0 0.8rem',
    },
    body1: {
      fontSize: '1.1rem',
      lineHeight: 1.6,
      marginBottom: '1rem',
    },
  },
});

const YouTubeSearchExportBlog = () => {
  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: { xs: 3, md: 5 }, borderRadius: 2 }}>
          <Typography variant="h1" component="h1" color="primary">
            How to Export YouTube Search Results to CSV: The Ultimate Guide
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 3 }}>
            YouTube's search results contain valuable data for researchers, marketers, and content creators. 
            But manually copying this information is tedious and inefficient. Whether you're conducting 
            competitor analysis, tracking video rankings, or gathering market research data, exporting 
            YouTube search results to CSV or Excel can save you hours of work. This comprehensive guide 
            will show you exactly how to extract and export YouTube search data with just a few clicks.
          </Typography>
          
          <Box sx={{ textAlign: 'center', my: 4 }}>
            <Button 
              variant="contained" 
              size="large" 
              component="a" 
              href="https://www.myfreshtools.com/youtube/searchResultsExport"
              target="_blank"
              rel="noopener noreferrer"
              color="primary"
            >
              Export Search Results Now
            </Button>
          </Box>
          
          <Divider sx={{ my: 4 }} />
          
          <Typography variant="h2">Why Export YouTube Search Results?</Typography>
          
          <Typography variant="body1">
            Exporting YouTube search results provides structured data you can analyze, sort, and share. Here are the top use cases:
          </Typography>
          
          <List sx={{ mb: 3 }}>
            <ListItem>
              <ListItemText primary="🔍 Competitive Analysis - Track competitors' video rankings for specific keywords" />
            </ListItem>
            <ListItem>
              <ListItemText primary="📈 SEO Research - Analyze top-performing videos in your niche to optimize your content" />
            </ListItem>
            <ListItem>
              <ListItemText primary="📊 Market Research - Gather data on trending topics and viewer preferences" />
            </ListItem>
            <ListItem>
              <ListItemText primary="📑 Academic Studies - Collect data for media or social science research" />
            </ListItem>
            <ListItem>
              <ListItemText primary="🤝 Client Reporting - Provide clients with structured data on their video performance" />
            </ListItem>
          </List>
          
          <Typography variant="h2">Method 1: Using MyFreshTools Search Results Exporter (Recommended)</Typography>
          
          <Typography variant="body1">
            The easiest way to export YouTube search results is using our specialized tool at MyFreshTools. Here's how it works:
          </Typography>
          
          <Box component="ol" sx={{ pl: 4, mb: 3 }}>
            <li>Visit <Link href="https://www.myfreshtools.com/youtube/searchResultsExport" target="_blank" color="primary">MyFreshTools Search Results Exporter</Link></li>
            <li>Enter your search query (e.g., "digital marketing tutorials")</li>
            <li>Select the country to get the SERP for the given country</li>
            <li>Click "Export to CSV" and download your structured data</li>
          </Box>
          
          <Typography variant="h2">What Data is Included in the Export?</Typography>
          
          <Typography variant="body1">
            Our YouTube search results exporter provides comprehensive data for each video in your search results:
          </Typography>
          
          <List dense sx={{ mb: 3 }}>
            <ListItem>
              <ListItemText primary="• Video Title" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Video URL" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Video ID" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Channel Name" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Channel URL" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Published Date" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Duration" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• View Count" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Like Count" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Comment Count" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Thumbnail URL" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Description Excerpt" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Search Ranking Position" />
            </ListItem>
          </List>
          
          <Box sx={{ backgroundColor: '#f5f5f5', p: 3, borderRadius: 1, mb: 4 }}>
            <Typography variant="h3" sx={{ mt: 0 }}>Pro Tip</Typography>
            <Typography variant="body1">
              For comprehensive analysis, export multiple pages of search results. YouTube typically shows 
              20 results per page, but our tool lets you export up to 100 results at once for more robust data sets.
            </Typography>
          </Box>
          
          <Typography variant="h2">Method 2: Manual Export Using YouTube API</Typography>
          
          <Typography variant="body1">
            Developers can access YouTube search data through the YouTube Data API. Here's a basic overview:
          </Typography>
          
          <Box component="ol" sx={{ pl: 4, mb: 3 }}>
            <li>Set up a project in Google Cloud Console</li>
            <li>Enable the YouTube Data API v3</li>
            <li>Generate API credentials</li>
            <li>Make requests to the search.list endpoint</li>
            <li>Parse the JSON response and convert to CSV format</li>
          </Box>
          
          <Typography variant="body1" sx={{ mb: 3 }}>
            While this method offers flexibility, it requires technical knowledge and is subject to API quotas. 
            For most users, our <Link href="https://www.myfreshtools.com/youtube/searchResultsExport" color="primary">YouTube search exporter</Link> is more efficient.
          </Typography>
          
          <Typography variant="h2">Method 3: Browser Extensions and Scripts</Typography>
          
          <Typography variant="body1">
            Some browser extensions and scripts claim to export YouTube search data, but consider these limitations:
          </Typography>
          
          <List sx={{ mb: 3 }}>
            <ListItem>
              <ListItemText 
                primary="⚠️ Security Concerns" 
                secondary="Extensions often require broad permissions that could compromise your privacy" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="📛 Limited Results" 
                secondary="Most only scrape the visible page (20 results) rather than comprehensive data" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="🔄 Maintenance Issues" 
                secondary="Extensions frequently break when YouTube updates its interface" 
              />
            </ListItem>
          </List>
          
          <Typography variant="h2">Advanced Applications of Exported Search Data</Typography>
          
          <Typography variant="body1">
            Once you have your YouTube search results in CSV format, you can perform powerful analyses:
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 4 }}>
            <Paper elevation={2} sx={{ p: 2 }}>
              <Typography variant="h3" sx={{ fontSize: '1.2rem', mt: 0 }}>SEO Optimization</Typography>
              <Typography variant="body2">
                Analyze title patterns, description length, and tags from top-ranking videos to improve your own content.
              </Typography>
            </Paper>
            <Paper elevation={2} sx={{ p: 2 }}>
              <Typography variant="h3" sx={{ fontSize: '1.2rem', mt: 0 }}>Trend Analysis</Typography>
              <Typography variant="body2">
                Track how specific videos rise or fall in rankings over time by exporting regular snapshots.
              </Typography>
            </Paper>
            <Paper elevation={2} sx={{ p: 2 }}>
              <Typography variant="h3" sx={{ fontSize: '1.2rem', mt: 0 }}>Content Gap Analysis</Typography>
              <Typography variant="body2">
                Identify underserved topics by analyzing the full spectrum of search results for your keywords.
              </Typography>
            </Paper>
            <Paper elevation={2} sx={{ p: 2 }}>
              <Typography variant="h3" sx={{ fontSize: '1.2rem', mt: 0 }}>Influencer Research</Typography>
              <Typography variant="body2">
                Discover top channels in your niche by analyzing channel distribution across search results.
              </Typography>
            </Paper>
          </Box>
          
          <Typography variant="h2">Frequently Asked Questions</Typography>
          
          <Typography variant="h3">Is exporting YouTube search results legal?</Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Yes, exporting publicly available search result data complies with YouTube's Terms of Service as long as you're not redistributing the actual video content.
          </Typography>
          
          <Typography variant="h3">Can I export results from specific date ranges?</Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Our tool allows filtering by upload date (last hour, today, this week, this month, or this year) before exporting.
          </Typography>
          
          <Typography variant="h3">How current is the exported data?</Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            The data reflects live search results at the moment of export, including real-time view counts and engagement metrics.
          </Typography>
          
          <Typography variant="h3">Can I export results from different countries?</Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Currently, results are based on your IP location. For country-specific results, you would need to use a VPN before exporting.
          </Typography>
          
          <Typography variant="h3">Is there a limit to how many results I can export?</Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            You can export up to 500 results per query. For larger datasets, you can perform multiple exports with different sorting options.
          </Typography>
          
          <Divider sx={{ my: 4 }} />
          
          <Typography variant="h2">Best Practices for YouTube Search Analysis</Typography>
          
          <Typography variant="body1" sx={{ mb: 2 }}>
            To get the most value from your exported YouTube search data:
          </Typography>
          
          <List sx={{ mb: 4 }}>
            <ListItem>
              <ListItemText 
                primary="1. Export Multiple Keywords" 
                secondary="It shows popular videos around the given keywords" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="2. Compare Over Time" 
                secondary="Export the same search weekly to track ranking changes" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="3. Analyze Metadata Patterns" 
                secondary="Look for common title structures, description elements, or tags in top videos" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="4. Filter by Duration" 
                secondary="Different video lengths perform better for different search intents" 
              />
            </ListItem>
          </List>
          
          <Box sx={{ 
            backgroundColor: '#e3f2fd',
            p: 4, 
            borderRadius: 2, 
            textAlign: 'center',
            border: '1px solid #bbdefb'
          }}>
            <Typography variant="h3" sx={{ mt: 0, mb: 2, color: 'primary.main' }}>
              Start Exporting YouTube Search Results Today
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Unlock powerful insights from YouTube search data without any technical skills required.
            </Typography>
            <Button 
              variant="contained" 
              size="large" 
              component="a" 
              href="https://www.myfreshtools.com/youtube/searchResultsExport"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ fontSize: '1.1rem', py: 1.5, px: 4 }}
              color="primary"
            >
              Export Search Results Now
            </Button>
          </Box>
          
          <Typography variant="body2" sx={{ mt: 4, fontStyle: 'italic' }}>
            Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </Typography>
        </Paper>
      </Container>
    </ThemeProvider>
  );
};

export default YouTubeSearchExportBlog;